// Predicates and bucketers for GameFiltersBar.
//
// Each predicate returns true when the game passes the filter. "any" is the
// universal pass-through. All time math is millisecond-based to stay
// timezone-honest with the rest of the league-status helpers.

import { getRewardTier, sortByStartDateAsc } from "./leagueStatus";

const DAY = 24 * 60 * 60 * 1000;

const safeStart = (g) => {
  if (!g?.start_date) return null;
  const t = new Date(g.start_date).getTime();
  return Number.isNaN(t) ? null : t;
};

const safeEnd = (g) => {
  if (!g?.end_date) return null;
  const t = new Date(g.end_date).getTime();
  return Number.isNaN(t) ? null : t;
};

export const matchesAsset = (game, value) => {
  if (value === "any") return true;
  const c = (game?.asset_category || "").toLowerCase();
  return c === value;
};

export const durationBucket = (game) => {
  const s = safeStart(game);
  const e = safeEnd(game);
  if (s == null || e == null || e <= s) return null;
  const days = (e - s) / DAY;
  if (days < 7) return "short";
  if (days < 28) return "medium";
  if (days < 90) return "long";
  return "epic";
};

export const matchesDuration = (game, value) => {
  if (value === "any") return true;
  return durationBucket(game) === value;
};

export const leagueTypeOf = (game) => {
  if (game?.is_cash_prize_league) return "cash";
  return game?.game_type || "public";
};

export const matchesType = (game, value) => {
  if (value === "any") return true;
  return leagueTypeOf(game) === value;
};

export const memberBucket = (count) => {
  const n = Number(count) || 0;
  if (n < 5) return "starting";
  if (n <= 50) return "small";
  if (n <= 100) return "medium";
  return "large";
};

export const matchesMembers = (game, value) => {
  if (value === "any") return true;
  return memberBucket(game?.members_count) === value;
};

export const matchesRewards = (game, value) => {
  if (value === "any") return true;
  // Coin-stack reward tiers only apply to public, non-cash leagues. A private
  // or cash league should never satisfy a tier filter even if its member count
  // would map to one — its rewards work differently.
  if (game?.is_cash_prize_league) return false;
  if (game?.game_type && game.game_type !== "public") return false;
  const tier = getRewardTier(game?.members_count).tier;
  return `tier-${tier}` === value;
};

export const startBucket = (game, now = Date.now()) => {
  const s = safeStart(game);
  if (s == null) return null;
  if (s <= now) return "live";
  const ms = s - now;
  // Use `<= DAY` to stay consistent with the "Starting Soon" badge rule in
  // leagueStatus.js (a league exactly 24h out still counts as "today").
  if (ms <= DAY) return "today";
  if (ms <= 7 * DAY) return "this-week";
  if (ms <= 14 * DAY) return "next-week";
  return "later";
};

export const matchesStartTime = (game, value, now = Date.now()) => {
  if (value === "any") return true;
  return startBucket(game, now) === value;
};

export const applyAllFilters = (games, filters) => {
  if (!Array.isArray(games)) return [];
  const now = Date.now();
  return games.filter(
    (g) =>
      matchesAsset(g, filters.asset) &&
      matchesDuration(g, filters.duration) &&
      matchesType(g, filters.type) &&
      matchesMembers(g, filters.members) &&
      matchesRewards(g, filters.rewards) &&
      matchesStartTime(g, filters.startTime, now)
  );
};

export const applySort = (games, sortKey) => {
  if (!Array.isArray(games)) return [];
  const copy = [...games];
  if (sortKey === "most-members") {
    copy.sort(
      (a, b) => (Number(b?.members_count) || 0) - (Number(a?.members_count) || 0)
    );
    return copy;
  }
  if (sortKey === "newest") {
    copy.sort((a, b) => {
      const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    return copy;
  }
  // default: starting-soon — delegate to the shared helper which buckets
  // cancelled/ended leagues to the bottom and falls back to chronological
  // ascending order for the rest.
  return sortByStartDateAsc(copy);
};
